import className from 'classnames/bind';
import styles from './Contact.module.scss';
import {object, string} from "yup";
import Button from "../Button/Button";
import dynamic from "next/dynamic";
import {API_FORM_CONTACT} from "@/lib/utils/constants";
import {useRef, useState} from "react";
import { toast } from 'sonner';

const cx = className.bind(styles);
const Formik = dynamic(() => import('formik').then(mod => mod.Formik));
const Field = dynamic(() => import('formik').then(mod => mod.Field));
const Form = dynamic(() => import('formik').then(mod => mod.Form));
const ReCAPTCHA = dynamic(() => import('react-google-recaptcha').then(mod => mod.default));

const validationSchema = object().shape({
  emailAddress: string().email('Invalid email address').required('Email is required'),
  subject: string(),
  fullName: string().min(5, 'Full name is too short').required('Full name is required'),
  message: string(),
});

export default function Contact({children, className}) {
  const recaptchaRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState('');

  const handleSubmit = async (values, formikBag) => {
    values.recaptcha = recaptchaValue;

    const response = await fetch(API_FORM_CONTACT, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(values),
    });

    const data = await response.json();

    // Notify user
    if (data.success) {
      toast.success(data.message);
    } else {
      toast.error(data.message);
    }

    // Reset form
    formikBag.resetForm();
    setRecaptchaValue('');
    if ('reset' in recaptchaRef.current) {
      recaptchaRef.current?.reset();
    } else {
      recaptchaRef.current?.retry();
    }
  };

  return (
    <div className={cx(['component', className])}>
      <div className={cx('colLeft')}>
        {children}
      </div>

      <div className={cx('colRight')}>
        <Formik
          initialValues={{
            emailAddress: '',
            fullName: '',
            subject: '',
            message: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({errors, touched, isSubmitting}) => {
            const isInvalidEmail = errors.emailAddress && touched.emailAddress;
            const isInvalidFullName = errors.fullName && touched.fullName;
            const hasError = isInvalidEmail || isInvalidFullName || !recaptchaValue;

            return (
              <Form className={cx(['form'])}>
                <div className={cx('formGroup')}>
                  <label htmlFor="fullName">Full name <span className={cx('required')}>(required)</span></label>
                  <Field
                    name="fullName"
                    type="text"
                    className={cx(['input', {'errorInput': isInvalidFullName}])}
                  />
                  {isInvalidFullName && <div className={cx('error')}>{errors.fullName}</div>}
                </div>

                <div className={cx('formGroup')}>
                  <label htmlFor="emailAddress">Email address <span
                    className={cx('required')}>(required)</span></label>
                  <Field
                    name="emailAddress"
                    type="email"
                    autoComplete="email"
                    className={cx(['input', {'errorInput': isInvalidEmail}])}
                  />
                  {isInvalidEmail && <div className={cx('error')}>{errors.emailAddress}</div>}
                </div>

                <div className={cx('formGroup')}>
                  <label htmlFor="subject">Subject</label>
                  <Field
                    name="subject"
                    type="text"
                    className={cx(['input'])}
                  />
                </div>

                <div className={cx('formGroup')}>
                  <label htmlFor="message">Message</label>
                  <Field
                    name="message"
                    as="textarea"
                    className={cx(['input', 'message'])}
                  />
                </div>

                <div className={cx('formGroup')}>
                  <ReCAPTCHA
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    ref={recaptchaRef}
                    onChange={(value) => setRecaptchaValue(value)}
                  />
                </div>

                <div className={cx('formGroup')}>
                  <Button
                    disabled={isSubmitting || hasError}
                    type="submit"
                    variant={`fill`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
